import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CartService } from '../../services/cart.service';
import { OrderService } from '../../services/order.service';
import { AuthService } from '../../services/auth.service';
import { PermissionsService } from '../../services/permissions.service';
import { OrderNotificationService } from '../../services/order-notification.service';
import { PushNotificationService } from '../../services/push-notification.service';

// Standard EU allergens list
const STANDARD_ALLERGENS = [
  'Gluten', 'Crustaceos', 'Huevos', 'Pescado', 'Cacahuetes',
  'Soja', 'Lacteos', 'Frutos de cascara', 'Apio', 'Mostaza',
  'Sesamo', 'Sulfitos', 'Altramuces', 'Moluscos',
];

@Component({
  selector: 'app-order-confirmation',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './order-confirmation.component.html',
  styleUrl: './order-confirmation.component.scss'
})
export class OrderConfirmationComponent implements OnInit {
  private router = inject(Router);
  private cartService = inject(CartService);
  private orderService = inject(OrderService);
  private authService = inject(AuthService);
  private permissionsService = inject(PermissionsService);
  private orderNotificationService = inject(OrderNotificationService);
  private pushService = inject(PushNotificationService);

  // Cart data
  cart = computed(() => this.cartService.cart());
  cartItems = computed(() => this.cart()?.items || []);
  totalItems = computed(() => this.cartService.totalItems());
  totalPrice = computed(() => this.cartService.totalPrice());

  // Snapshot of order data (preserved after cart is cleared)
  submittedOrderInfo = signal<{ restaurantName: string; total: number; mode: string } | null>(null);

  // Collapsible sections
  showSummary = signal(false);
  showAllergens = signal(false);
  showCustomerData = signal(false);

  // Allergens
  standardAllergens = STANDARD_ALLERGENS;
  selectedAllergens = signal<string[]>([]);
  customAllergen = '';

  // Notes
  specialNotes = '';

  // Customer data (auto-filled, read-only display)
  customerName = '';
  customerPhone = '';

  // State
  submitting = signal(false);
  submitted = signal(false);
  orderId = signal('');
  errorMessage = signal('');
  notificationsEnabled = signal(false);
  notificationsDenied = signal(false);

  ngOnInit(): void {
    const state = history.state;
    // Allow access from cart navigation OR if cart has items (post-login redirect)
    const hasCart = this.cart() && this.cartItems().length > 0;
    if (!state?.fromCart && !hasCart) {
      this.router.navigate(['/dashboard']);
      return;
    }

    const user = this.authService.getCurrentUser();
    if (user) {
      this.customerName = user.name || '';
    }

    const notifState = this.permissionsService.getPermissionStates().notifications;
    this.notificationsEnabled.set(notifState.granted);
    this.notificationsDenied.set(notifState.denied);
  }

  async enableNotifications(): Promise<void> {
    const result = await this.permissionsService.requestNotificationPermission();
    this.notificationsEnabled.set(result.granted);
    this.notificationsDenied.set(result.denied);

    if (result.granted) {
      this.permissionsService.showNotification('Notificaciones activadas', {
        body: 'Recibiras avisos cuando tu pedido cambie de estado.',
        tag: 'notif-enabled',
      });
      // Subscribe to Web Push for background notifications
      this.pushService.subscribeToPush();
    }
  }

  // Section toggles
  toggleSummary(): void { this.showSummary.update(v => !v); }
  toggleAllergens(): void { this.showAllergens.update(v => !v); }
  toggleCustomerData(): void { this.showCustomerData.update(v => !v); }

  // Allergen management
  toggleAllergen(allergen: string): void {
    this.selectedAllergens.update(list => {
      if (list.includes(allergen)) {
        return list.filter(a => a !== allergen);
      }
      return [...list, allergen];
    });
  }

  addCustomAllergen(): void {
    const sanitized = this.customAllergen.trim().substring(0, 50).replace(/[<>{}]/g, '');
    if (!sanitized) return;
    if (this.selectedAllergens().includes(sanitized)) return;
    this.selectedAllergens.update(list => [...list, sanitized]);
    this.customAllergen = '';
  }

  removeAllergen(allergen: string): void {
    this.selectedAllergens.update(list => list.filter(a => a !== allergen));
  }

  get orderModeText(): string {
    const mode = this.cart()?.order_mode;
    switch (mode) {
      case 'dine-in': return 'En mesa';
      case 'takeaway': return 'Recoger';
      case 'delivery': return 'Delivery';
      default: return 'En mesa';
    }
  }

  get orderModeIcon(): string {
    const mode = this.cart()?.order_mode;
    switch (mode) {
      case 'dine-in': return '🍽️';
      case 'takeaway': return '🥡';
      case 'delivery': return '🛵';
      default: return '🍽️';
    }
  }

  formatPrice(price: number): string {
    return this.cartService.formatPrice(price);
  }

  getItemTotal(item: { price: number; quantity: number }): number {
    return item.price * item.quantity;
  }

  getCustomizationsText(customizations: any[]): string {
    return this.cartService.getCustomizationsText(customizations);
  }

  submitOrder(): void {
    this.submittedOrderInfo.set({
      restaurantName: this.cart()?.restaurant_name || '',
      total: this.totalPrice(),
      mode: this.cart()?.order_mode || 'dine-in',
    });

    this.submitting.set(true);
    this.errorMessage.set('');

    // Build special requests from allergens + notes
    const parts: string[] = [];
    if (this.selectedAllergens().length > 0) {
      parts.push('ALERGENOS: ' + this.selectedAllergens().join(', '));
    }
    if (this.specialNotes.trim()) {
      parts.push(this.specialNotes.trim());
    }
    const specialRequests = parts.join(' | ') || undefined;

    this.orderService.submitOrder(
      this.customerName,
      this.customerPhone || undefined,
      specialRequests,
      this.selectedAllergens().length > 0 ? this.selectedAllergens() : undefined
    ).subscribe({
      next: (response) => {
        this.submitting.set(false);
        if (response.success) {
          this.submitted.set(true);
          const oid = response.order_id || '';
          this.orderId.set(oid);
          if (oid) {
            this.orderNotificationService.subscribeToOrder(oid);
          }
        } else {
          this.errorMessage.set(response.message || 'Error al enviar el pedido');
        }
      },
      error: () => {
        this.submitting.set(false);
        this.errorMessage.set('Error de conexion. Intentalo de nuevo.');
      }
    });
  }

  goBack(): void {
    window.history.back();
  }

  goToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }
}
